<template>
	<div id="asl-interface-container">
		<div id="asl-interface-main">
			<div class="capture-window-container" aria-label="Camera view" role="group">
				<div class="loading-message" v-if="isSendingSign">
					<svg class="loading-dots" xmlns="http://www.w3.org/2000/svg" width="80" height="15" viewBox="0 0 80 15" fill="none" aria-hidden="true">
						<ellipse cx="7.27273" cy="7.36257" rx="7.27273" ry="7.27273" fill="#C4C4C4"/>
						<ellipse cx="40" cy="7.36257" rx="7.27273" ry="7.27273" fill="#B1B1B1"/>
						<ellipse cx="72.7273" cy="7.36257" rx="7.27273" ry="7.27273" fill="#8A8A8A"/>
					</svg>
					<span>Loading...</span>
				</div>
				<RecordingBadge v-if="isRecording" />
				<video ref="video" id="capture-window" class="video-player" autoplay="autoplay" :inert="isSendingSign" aria-label="Camera feed" playsinline></video>
			</div>
			<div id="asl-actions">
				<button class="standard-button primary has-icons record" @click="toggleRecording" :disabled="this.mediaRecorder === null || isSendingSign">
					<svg class="sending-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" v-if="isSendingSign">
						<path d="M20.27,6.37A10.35,10.35,0,0,0,19,4.89L18,6A8.6,8.6,0,0,1,19,7.22,8.5,8.5,0,1,1,12,3.5V2a10,10,0,1,0,8.27,4.37Z"/>
					</svg>
					<svg v-if="isRecording && !isSendingSign" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
						<rect x="3" y="3" width="18" height="18"/>
					</svg>
					<svg v-if="!isRecording && !isSendingSign" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 18" aria-hidden="true">
						<path
							d="M8.49999 17.5102C3.80999 17.5102 -0.0100098 13.6902 -0.0100098 9.00023C-0.0100098 4.31023 3.80999 0.490234 8.49999 0.490234C13.19 0.490234 17.01 4.31023 17.01 9.00023C17.01 13.6902 13.19 17.5102 8.49999 17.5102ZM8.49999 1.99023C4.63999 1.99023 1.48999 5.13023 1.48999 9.00023C1.48999 12.8702 4.62999 16.0102 8.49999 16.0102C12.37 16.0102 15.51 12.8702 15.51 9.00023C15.51 5.13023 12.37 1.99023 8.49999 1.99023Z">
						</path>
						<path
							d="M8.50001 11.9898C10.1513 11.9898 11.49 10.6511 11.49 8.99977C11.49 7.34843 10.1513 6.00977 8.50001 6.00977C6.84868 6.00977 5.51001 7.34843 5.51001 8.99977C5.51001 10.6511 6.84868 11.9898 8.50001 11.9898Z">
						</path>
					</svg>
					<span>{{ isSendingSign ? `${$t("sendingSign")}...` : (isRecording ? $t("stopSignRecording") : $t("startSignRecording")) }}</span></button>
				<div class="camera-selector">
					<button ref="camera" id="camera" class="standard-button secondary has-icons" @click="expandCollapseCameraMenu" :aria-expanded="cameraListExpanded.toString()" :disabled="this.mediaRecorder === null">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 24" aria-hidden="true">
							<path fill-rule="evenodd" clip-rule="evenodd"
								d="M17.6213 13.1213C17.0587 13.6839 16.2956 14 15.5 14H13.21V15.51H14.91C15.2701 15.5086 15.6216 15.6196 15.9156 15.8276C16.2096 16.0355 16.4314 16.33 16.55 16.67L17.61 19.67C17.7021 19.9322 17.7305 20.2125 17.6927 20.4879C17.655 20.7632 17.5522 21.0256 17.3929 21.2533C17.2336 21.481 17.0224 21.6675 16.7767 21.7973C16.531 21.9272 16.2579 21.9967 15.98 22H8.98C8.70092 21.9972 8.42656 21.9277 8.17981 21.7973C7.93306 21.6669 7.72108 21.4794 7.56155 21.2503C7.40203 21.0213 7.29959 20.7575 7.26279 20.4808C7.22599 20.2042 7.25589 19.9227 7.35 19.66L8.41 16.66C8.52864 16.32 8.75041 16.0255 9.04439 15.8176C9.33837 15.6097 9.68991 15.4986 10.05 15.5H11.75V14H9.5C8.70435 14 7.94129 13.6839 7.37868 13.1213C6.81607 12.5587 6.5 11.7956 6.5 11V5C6.5 4.20435 6.81607 3.44129 7.37868 2.87868C7.94129 2.31607 8.70435 2 9.5 2H15.5C16.2956 2 17.0587 2.31607 17.6213 2.87868C18.1839 3.44129 18.5 4.20435 18.5 5V11C18.5 11.7956 18.1839 12.5587 17.6213 13.1213ZM16.23 20.16L15.17 17.16C15.1566 17.1125 15.1276 17.0709 15.0877 17.0419C15.0478 17.0129 14.9993 16.9981 14.95 17H10.05C10.0188 17.0153 9.99163 17.0378 9.97077 17.0656C9.94991 17.0934 9.93594 17.1257 9.93 17.16L8.87 20.16C8.84878 20.1946 8.83755 20.2344 8.83755 20.275C8.83755 20.3156 8.84878 20.3554 8.87 20.39C8.88811 20.4214 8.91444 20.4473 8.94614 20.4649C8.97785 20.4826 9.01375 20.4912 9.05 20.49H16.05C16.0863 20.4912 16.1222 20.4826 16.1539 20.4649C16.1856 20.4473 16.2119 20.4214 16.23 20.39C16.2512 20.3554 16.2625 20.3156 16.2625 20.275C16.2625 20.2344 16.2512 20.1946 16.23 20.16ZM8 5V11C8 11.3978 8.15804 11.7794 8.43934 12.0607C8.72064 12.342 9.10217 12.5 9.5 12.5H15.5C15.8978 12.5 16.2794 12.342 16.5607 12.0607C16.842 11.7794 17 11.3978 17 11V5C17 4.60218 16.842 4.22064 16.5607 3.93934C16.2794 3.65804 15.8978 3.5 15.5 3.5H9.5C9.10217 3.5 8.72064 3.65804 8.43934 3.93934C8.15804 4.22064 8 4.60218 8 5ZM10.8333 5.50559C11.3266 5.17595 11.9067 5 12.5 5C13.2957 5 14.0587 5.31607 14.6213 5.87868C15.1839 6.44129 15.5 7.20435 15.5 8C15.5 8.59334 15.3241 9.17336 14.9944 9.66671C14.6648 10.1601 14.1962 10.5446 13.6481 10.7716C13.0999 10.9987 12.4967 11.0581 11.9147 10.9424C11.3328 10.8266 10.7982 10.5409 10.3787 10.1213C9.95912 9.70176 9.6734 9.16721 9.55765 8.58527C9.44189 8.00333 9.5013 7.40013 9.72836 6.85195C9.95543 6.30377 10.3399 5.83524 10.8333 5.50559ZM11.6666 9.2472C11.9133 9.41203 12.2033 9.5 12.5 9.5C12.8978 9.5 13.2794 9.34196 13.5607 9.06066C13.842 8.77936 14 8.39783 14 8C14 7.70333 13.912 7.41332 13.7472 7.16664C13.5824 6.91997 13.3481 6.72771 13.074 6.61418C12.7999 6.50065 12.4983 6.47094 12.2074 6.52882C11.9164 6.5867 11.6491 6.72956 11.4393 6.93934C11.2296 7.14912 11.0867 7.41639 11.0288 7.70736C10.9709 7.99834 11.0007 8.29994 11.1142 8.57403C11.2277 8.84811 11.42 9.08238 11.6666 9.2472Z">
							</path>
						</svg>
						<span>Camera</span>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
							<path
								d="M11.9902 16.0195L5.49023 9.55953L6.55023 8.51953L11.9902 13.9195L17.4302 8.51953L18.4902 9.55953L11.9902 16.0195Z">
							</path>
						</svg>
					</button>
					<div role="listbox" :class="'camera-select-dropdown' + (cameraListExpanded ? '' : ' hide')"
						aria-label="Choose a camera">
						<button ref="options" v-for="(option, index) in options" class="camera-select-option"
							role="option" :data-value="option.value"
							:aria-selected="((option.value === selectedID) || (!selectedID && index === 0)).toString()"
							@click="selectCamera(option.value)" :tabindex="index === focusedRadio ? null : '-1'"
							@keydown.up.prevent="focusOnPreviousDevice" @keydown.down.prevent="focusOnNextDevice">
							<div class="custom-radio">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
									focusable="false" aria-hidden="true">
									<g>
										<circle cx="12" cy="12" r="7"></circle>
									</g>
								</svg>
							</div>
							<span class="custom-radio-text">{{ option.text }}</span>
						</button>
					</div>
				</div>
			</div>
		</div>
		<ASLFeedback :recognizedSign="foundTranslation" :signID="lastSignID" v-if="showFeedbackPopup === true" @dismiss-feedback="dismissFeedbackPopup" />
		<ASLNotificationBar :isError="useErrorNotificationStyle" :title="notificationTitle" :message="notificationMessage" @dismiss-notification="dismissNotification" :visible="showNotificationBar === true" />
	</div>
</template>

<script>

import { Analytics } from '../analytics.js';
import ASLFeedback from './ASLFeedback.vue';
import ASLNotificationBar from './ASLNotificationBar.vue';
import RecordingBadge from './RecordingBadge.vue';

export default {
	name: 'ASLInterface',
	components: {
		ASLNotificationBar,
		RecordingBadge,
		ASLFeedback
	},
	data() {
		return {
			recordedBlobs: [],
			mediaRecorder: null,
			isRecording: false,
			isSendingSign: false,
			predictionToShow: null,
			result: null,
			isCancelled: false,
			startTime: null,
			endTime: null,
			sendTime: null,
			returnTime: null,
			videoDuration: null,
			responseTime: null,
			base64Time: null,
			options: [],
			selectedID: null,
			foundTranslation: "",
			cameraListExpanded: false,
			focusedRadio: 0,
			showNotificationBar: false,
			aslRecordingTimer: null,             // Limit sign recording duration
			aslTranslationTimer: null,           // Prevent 'hang' if/when server doesn't respond (quickly enough)
			notificationDismissTimer: null,      // Auto-dismiss notification bar
			useErrorNotificationStyle: true,
			notificationMessage: "",
			notificationTitle: "",
			showFeedbackPopup: false,
			lastSignID: null,
			// WebSocket and slice config
			aslSocket: null,
			socketOpen: false,
			sliceLength: 500,
			buffer: []                            // Holds video segments until socket is open
		};
	},

	beforeDestroy() {
		this.closeSocket();
		this.clearASLRecordingTimer();
		this.clearASLTranslationTimer();
		this.clearNotificationDismissTimer();
	},

	mounted() {
		this.clearNotificationDismissTimer();

		navigator.mediaDevices.getUserMedia({ video: true, audio: false })
			.then((stream) => {
				this.mediaRecorder = this.createMediaRecorder(stream);
				if (this.$refs.video) {
					this.$refs.video.srcObject = stream;
				}
			})
			.catch((err) => {
				this.setNotificationMessage("Camera permissions not granted. Please allow camera access.");
			});
		if (!navigator.mediaDevices?.enumerateDevices) {
			console.error("enumerateDevices() not supported.");
		} else {
			// List cameras and microphones.
			this.listDevices();
		}
	},

	methods: {
		createMediaRecorder(stream) {
			let options;
			const isChromeBrowser = navigator.userAgent.toLowerCase().includes('chrome') && !navigator.userAgent.includes('edge') && !navigator.userAgent.includes('opr') && !navigator.userAgent.includes('crios');
			if (isChromeBrowser) {
				options = { bitsPerSecond: 1000000, mimeType: 'video/webm;codecs=VP9' };
			} else {
				options = { bitsPerSecond: 1000000 };
			}
			let mr;
			try {
				mr = new MediaRecorder(stream, options);
			} catch (e) {
				// fallback if the browser doesn't accept the mimeType
				mr = new MediaRecorder(stream);
			}
			mr.ondataavailable = this.onDataAvailable;
			mr.onstop = this.onStop;
			return mr;
		},

		getProtocol() {
			return window.location.protocol;
		},
		getHostName() {
			return window.location.hostname;
		},
		getPort() {
			return window.location.port;
		},
		getWebSocketUrl() {
			const protocol = this.getProtocol() === 'https:' ? 'wss:' : 'ws:';
			const hostName = this.getHostName();
			const port = this.getPort();
			return `${protocol}//${hostName}:${port}/asl`;
		},

		// Open websocket to sign-speak streaming endpoint (reused across recordings)
		connectSocket() {
			if (this.aslSocket && this.socketOpen) return Promise.resolve();
			return new Promise((resolve, reject) => {
				try {
					this.aslSocket = new WebSocket(this.getWebSocketUrl());
					// this.aslSocket.binaryType = 'arraybuffer';
					this.aslSocket.addEventListener('open', () => {
						this.socketOpen = true;
						// Flush buffered messages
						if (this.buffer.length > 0) {
							console.debug('ASL socket opened; flushing buffer of', this.buffer.length, 'elements');
							this.buffer.forEach((msg) => {
								// NOTE: Depends on handleSocketMessage expecting an event with 'data' property
								//       and not accessing any other property of the event!
								const fakeEvent = { data: msg };
								this.handleSocketMessage(fakeEvent);
							});
							this.buffer.length = 0;
						}
					});
					this.aslSocket.addEventListener('message', this.handleSocketMessage);
					this.aslSocket.addEventListener('error', (err) => {
						console.error('ASL socket error', err);
					});
					this.aslSocket.addEventListener('close', () => {
						console.debug('ASL socket closed');
						this.socketOpen = false;
						this.aslSocket = null;
					});
					resolve();

				} catch (err) {
					console.error('Error connecting to ASL socket:', err);
					reject(err);
				}
			});
		},

		// Close websocket to sign-speak streaming endpoint
		closeSocket() {
			if (this.aslSocket) {
				try {
					this.aslSocket.removeEventListener('message', this.handleSocketMessage);
					this.aslSocket.close();
				} catch (e) {
					// ignore
				}
				this.aslSocket = null;
				this.socketOpen = false;
			}
		},

		// Handle message FROM sign-speak over the websocket to sign-speak streaming endpoint
		handleSocketMessage(event) {
			let data = null;

			// Safe/appropriate since we're using "single_recognition_mode: true" with the ASL API (vs. a streaming approach)
			this.isSendingSign = false;

			// Handle case where server sends a response after we've already timed out
			if ( ! this.aslTranslationTimer ) {
				// We've already timed out; ignore this response and cancelled/cleared the timer
				console.debug('Received and dropped response from ASL server after timeout');
				return;
			}

			// Clear the translation timer since we got a response
			this.clearASLTranslationTimer();

			try {
				if (event.data === 'OUT_OF_QUOTA') {
					data = 'OUT_OF_QUOTA';
				} else if (typeof event.data === 'string') {
					data = JSON.parse(event.data);
				} else if (event.data instanceof ArrayBuffer) {
					// server probably won't send ArrayBuffer for JSON, but handle defensively
					const text = new TextDecoder().decode(new Uint8Array(event.data));
					data = JSON.parse(text);
				}
			} catch (err) {
				console.warn('Received non-JSON from recognition socket:', event.data);
				return;
			}

			if (!data) return;
			if (data === 'OUT_OF_QUOTA') {
				this.setNotificationMessage('Out of quota for sign recognition.');
				// The OUT_OF_QUOTA message comes in "quickly", while the user is likely still recording
				// and sending video. So, need to stop the mediaRecorder and set various flags accordingly.
				if (this.mediaRecorder && this.isRecording) {
					this.mediaRecorder.stop();
				}
				this.result = '';
				this.isRecording = false;
				this.isSendingSign = false;
				this.isCancelled = true;
				this.clearASLRecordingTimer();
				this.clearASLTranslationTimer();
				this.clearNotificationDismissTimer();
				return;
			}

			for (const pred of data.prediction || []) {
				const finished = pred.finished;
				let prediction = pred.prediction;
				let confidence = pred.confidence;
				const feedback_id = pred.feedback_id || pred.feedbackId || null;

				try {
					if (typeof confidence === 'string') {
						confidence = JSON.parse(confidence);
					}
				} catch (e) {
					confidence = parseFloat(confidence) || 0;
				}

				if (prediction && typeof prediction === 'string') {
					// Some responses wrap results in JSON strings
					try {
						const parsed = JSON.parse(prediction);
						if (parsed && parsed.prediction) {
							prediction = parsed.prediction[0].prediction;
						}
					} catch(e) {
						// not JSON; use as-is
					}
				}

				if (prediction) {
					this.lastSignID = feedback_id;
					const clean = prediction.replace(/[^A-Za-z0-9\s]/g, '').replace(/\s{2,}/g, ' ');
					this.foundTranslation = clean;

					const highConfidence = (typeof confidence === 'number') ? (confidence >= -0.7 && confidence !== 0) : (parseFloat(confidence) >= -0.7 && parseFloat(confidence) !== 0);

					if (highConfidence) {
						this.setNotificationMessage(this.foundTranslation, false);
						this.predictionToShow = clean;
						if (window.ar && typeof ar.submitTextCommand === 'function') {
							ar.submitTextCommand(this.foundTranslation);
						}
					} else {
						this.setNotificationMessage('Sign not recognized.', true);
					}
				} else if ( !confidence) {
					this.setNotificationMessage('Sign not recognized.', true);
				}
			}
		},

		clearASLRecordingTimer() {
			clearTimeout(this.aslRecordingTimer);
			this.aslRecordingTimer = undefined;
		},

		setASLNotificationTimer() {
			const aslRecordingTimeout = 30000;
			this.clearASLRecordingTimer();
			this.aslRecordingTimer = setTimeout(() => {
				if (this.isRecording) {
					this.toggleCancel();
					this.setNotificationMessage(`Sign recording is limited to ${aslRecordingTimeout / 1000} seconds. Your sign was not sent. Please try again.`);
					this.clearASLRecordingTimer();
				}
			}, aslRecordingTimeout);
		},

		clearASLTranslationTimer() {
			clearTimeout(this.aslTranslationTimer);
			this.aslTranslationTimer = undefined;
		},

		setASLTranslationTimer() {
			const aslTranslationTimeout = 15000;
			this.clearASLTranslationTimer();
			this.aslTranslationTimer = setTimeout(() => {
				if (this.isSendingSign) {
					this.isSendingSign = false;
					this.setNotificationMessage(`Sign translation timed out after ${aslTranslationTimeout / 1000} seconds. Please try again.`);
					this.clearASLTranslationTimer();
				}
			}, aslTranslationTimeout);
		},

		clearNotificationDismissTimer() {
			clearTimeout(this.notificationDismissTimer);
		},

		setNotificationMessage(text, isError = true) {
			this.useErrorNotificationStyle = isError;
			this.notificationTitle = "";
			this.notificationTitle = isError === true ? "Error" : "Last search";
			this.clearNotificationDismissTimer();
			this.notificationMessage = text;
			this.showNotificationBar = true
			this.notificationDismissTimer = setTimeout(() => {
				this.dismissNotification(isError);
			}, 15000);
		},

		dismissNotification(isError = false) {
			this.clearNotificationDismissTimer();
			this.showNotificationBar = false;
			if(!isError) {
				// If a sign is recognized, show the feedback modal after the notification bar either auto-dismisses or is closed by the user
				this.showFeedbackPopup = true;
			} else {
				// Otherwise, if an error occurs, set focus back to the page heading after the notification bar is dismissed.
				document.querySelector("h1")?.focus();
			}
		},

		dismissFeedbackPopup() {
			// Hide the ASL feedback popup
			this.showFeedbackPopup = false;
			document.querySelector("h1")?.focus();
		},

		// methods for shifting focus between fake radio buttons without actually selecting them. This allows us to disconnect focus from selection to make keyboard navigation a little easier.
		focusOnPreviousDevice() {
			if (this.options.length <= 1) return; // We don't need to shift focus around if there's only one device available.
			if (this.focusedRadio > 0) {
				this.focusedRadio--;
				this.$refs.options[this.focusedRadio].focus();
			}
		},

		focusOnNextDevice() {
			if (this.options.length <= 1) return; // We don't need to shift focus around if there's only one device available.
			if (this.focusedRadio < this.options.length - 1) {
				this.focusedRadio++;
				this.$refs.options[this.focusedRadio].focus();
			}
		},

		listDevices() {
			this.options = [];
			navigator.mediaDevices
				.enumerateDevices()
				.then((devices) => {
					devices.forEach((device) => {
						// Handy for debugging:
						// console.debug(`${device.kind}: ${device.label} id = ${device.deviceId}`);
						if (device.kind == 'videoinput') {
							this.options.push({ text: device.label, value: device.deviceId });
						}
					});
				})
				.catch((err) => {
					console.error(`${err.name}: ${err.message}`);
				});
		},

		expandCollapseCameraMenu(event) {
			let initialState = this.cameraListExpanded;
			this.cameraListExpanded = !this.cameraListExpanded;
			if (initialState === false && this.$refs.options[0]) {
				this.$refs.options[0].focus(); // FIX: This isn't working, probably because it's firing off before the class gets removed from the first button. I don't want to use a timeout because that can break easily.
			}
		},

		selectCamera(id) {
			this.selectedID = id;
			this.onChange();
			this.cameraListExpanded = false;
			this.$refs.camera.focus();
		},

		onChange(event) { //when camera drop down option changes
			//this.result = this.selected; //testing
			navigator.mediaDevices.getUserMedia({ video: { deviceId: this.selectedID }, audio: false })
				.then((stream) => {
					this.mediaRecorder = new MediaRecorder(stream);
					this.mediaRecorder.onstop = this.onStop;
					this.mediaRecorder.ondataavailable = this.onDataAvailable;
					this.$refs.video.srcObject = stream;
				});
		},

		async toggleRecording() {
			if (this.isRecording) {
				// Stop recording
				this.endTime = new Date();
				this.videoDuration = (this.endTime - this.startTime) / 1000;

				this.mediaRecorder.stop();
				this.foundTranslation = 'Your command is being processed, please wait...';
				this.isSendingSign = true;
				this.setASLTranslationTimer();
			} else {
				// Start recording
				this.dismissNotification(true);
				this.startTime = new Date();
				try {
					await this.connectSocket();

				} catch (err) {
					console.warn('Could not connect ASL websocket, will fallback to legacy upload', err);
				}
				this.mediaRecorder.start(this.sliceLength);
				this.recordedBlobs = [];
				this.result = '';
				this.setASLNotificationTimer();
			}
			this.isRecording = !this.isRecording;
		},

		toggleCancel() {
			if (this.isRecording) {
				this.mediaRecorder.stop();
				this.result = '';
				this.isCancelled = true;
				this.isRecording = false;
				this.setNotificationMessage("Recording canceled. Your sign was not sent.");
			}
		},

		onDataAvailable(event) {
			if ( this.isCancelled ) {
				// If recording was cancelled, ignore any dataavailable events
				console.debug('onDataAvailable message ignored due to cancellation');
				return;
			}

			// Buffer the data if the socket is not yet fully open
			if ( this.socketOpen && this.aslSocket.readyState !== WebSocket.OPEN) {
				console.debug('ASL socket not yet fully open; buffering data until it is');
				this.buffer.push(event.data);
				return;
			}

			if (event.data && event.data.size > 0) {
				if (this.aslSocket && this.socketOpen) {
					// send raw ArrayBuffer to websocket
					event.data.arrayBuffer().then((ab) => {
						try {
							this.aslSocket.send(ab);
						} catch (e) {
							console.error('Error sending chunk to ASL socket', e);
						}
					});
				} else {
					// fallback to collecting blobs for legacy upload
					console.debug('Using fallback logic to collect blobs for legacy upload');
					this.recordedBlobs.push(event.data);
				}
			}
		},

		onStop(event) {
			this.lastSignID = null;
			if (!this.isCancelled) {
				const lastChunk = event.data;
				if (lastChunk && lastChunk.size > 0) {
					this.recordedBlobs.push(lastChunk);
				}
				this.sendTime = new Date();
				this.base64Time = (this.sendTime - this.endTime) / 1000;
				this.isSendingSign = true;
				try {
					// Send 'NEXT' to ASL socket to indicate end of stream
					setTimeout(() => {
						if ( this.aslSocket ) {
							this.aslSocket.send('NEXT');
						}
					}, 300);
				} catch (e) {
					console.error('Error sending NEXT to ASL socket', e);
					this.setNotificationMessage('Sign recognition socket error.');
					this.isSendingSign = false;
				}
			}
			this.isCancelled = false;
		},
	},
};
</script>

<style lang="scss" scoped>
* {
	box-sizing: border-box;
}

.select {
	width: 60px;
	max-height: 60px;
	font-size: 11px;
}

#asl-interface-container {
	display: flex;
	flex: 1;
	flex-direction: column;
	background-color: $ar-background-color;
}

#asl-interface-main {
	display: grid;
	grid-template-rows: 1fr min-content;
	height: 100%;
	align-self: center;
	margin-top: 1.5rem;
	margin-right: 2rem;
	margin-left: 2rem;

	@include tp {
		display: flex;
		flex-direction: column;
		margin-top: 2.45rem;
	}
}

#asl-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: center;
	width: 100%;
	margin-top: 0;
	margin-bottom: 2.4rem;
	gap: 1rem;
	text-align: center;

	@include tp {
		position: relative;
		bottom: unset;
		left: unset;
		margin-top: 1.4rem;
		margin-bottom: 0;
	}

	& .record {
		
		&:disabled {
			color: $xfinity-grey-80;
		}

		@media(prefers-reduced-motion: no-preference) {
			& .sending-spinner { 
				animation-name: rotate;
				animation-duration: 3s;
				animation-timing-function: linear;
				animation-direction: normal;
				animation-iteration-count: infinite;
			}
		}
	}
}

.camera-selector {
	position: relative;
	display: flex;
	flex-direction: column;
	
	& .camera-select-dropdown:not(.hide) {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		position: absolute;
		bottom: 100%;
		background-color: white;
		border-radius: .5rem;
		padding: 1rem 1.5rem;
		font-size: 1rem;
		font-family: 'DM Sans', sans-serif;
		color: $xfinity-grey-80;
		width: 100%;
		
		@include tp {
			right: 0;
			top: 100%;
			bottom: unset;
			width: 20rem;
		}

		@include md {
			right: unset;
		}

		& .camera-select-option {
			padding: 0;
			display: flex;
			flex-direction: row;
			flex-wrap: nowrap;
			align-items: center;
			height: 1.5rem;
			background-color: transparent;
			border: none;
			gap: 1rem;

			& .custom-radio {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 1.5rem;
				height: 1.5rem;
				border: 0.0625rem solid $xfinity-grey-80;
				border-radius: 100%;
				flex-shrink: 0;
			}
			
			&[aria-selected='true'] .custom-radio {
				border-width: 0.125rem;
				border-color: $xfinity-purple-60;
				
				& svg circle {
					fill: $xfinity-purple-60;
					width: .875rem;
					height: .875rem;
				}
			}
			

			& .custom-radio-text {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&:hover {
				cursor: pointer;
			}

			&:focus {
				// This removes the normal focus outline because we'll be putting one on the "fake" radio instead
				outline: none;

				& .custom-radio {
					// Custom radio focus styles
					@include focus-outline;
					outline-color: $xfinity-purple-60;
				}
			}
		}
	}
}

#camera {
	& svg {
		fill: $xfinity-grey-lighter;
		height: 1.5rem;
	}

	&:hover,
	&:focus {
		& svg {
			fill: $xfinity-grey-darker;
		}
	}
}

.standard-button {
	border-radius: .5rem;
	margin-right: 0;
	width: 92vw;
	padding: .875rem 1.875rem;

	@include tp {
		width: auto;
	}
}

.processing {
	opacity: 50%;
}

video {
	display: block;
}

.capture-window-container {
	position: relative;
	border-radius: .25em;
	overflow: hidden;
	margin-bottom: 1rem;
}

.loading-message {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	font-size: 1rem;
	line-height: 1.5rem;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: $xfinity-grey-80;

	& .loading-dots {
		overflow: visible;
	}

	@media(prefers-reduced-motion: no-preference) {
		& .loading-dots ellipse {
			$dot-duration: 2s;
			animation-name: loading-dot-pulse;
			animation-duration: $dot-duration;
			animation-timing-function: linear;
			animation-iteration-count: infinite;
			animation-direction: normal;
			transform-origin: center;
	
			&:nth-child(1) {
				animation-delay: calc($dot-duration * 0.125);
			}
	
			&:nth-child(2) {
				animation-delay: calc($dot-duration * 0.25);
			}
	
			&:nth-child(3) {
				animation-delay: calc($dot-duration * 0.5);
			}
		}
	}
}

#capture-window {
	width: 100%;
	height: 100%;
	background-color: black;
	border-radius: .25em;

	@include tp {
		width: 90vw; // aspect ratio 16x9
		height: 50.625vw; // calc(90vw * 9 / 16)
	}

	@include lg {
		width: 50rem; //800px; aspect ratio 16x9
		height: 28.125rem; // 450px
	}
}

.hide {
	display: none;
}

.standard-button.has-icons {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: nowrap;
	gap: 0.5rem;
	line-height: 1.5;

	& svg {
		fill: $xfinity-grey-darker;
		height: 1.125rem;
	}

	&:hover svg, &:focus svg {
		fill: $xfinity-grey-05;
	}

	& span {
		display: block;
	}
}

/* keep for now.  will be removed later */
.asl-results {
	margin-top: 1rem;
}


@keyframes loading-dot-pulse {
	0% {
		transform: scale(1);
	}

	50% {
		transform: scale(1.25);
	}

	100% {
		transform: scale(1);
	}
}

@keyframes rotate {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}
</style>
